import {scope} from '../settings';
import moment from 'moment';

const dollarAmount = cents => {
  return `$${(cents / 100).toFixed(2)}`;
};

const getPasswordErrors = (password, passwordConfirmation, localeText) => {
  function containsUppercase(str) {
    return /[A-Z]/.test(str);
  }

  function containsLowercase(str) {
    return /[a-z]/.test(str);
  }

  function containsNumber(str) {
    return /[0-9]/.test(str);
  }

  function containsSpecialCharacter(str) {
    return /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(str);
  }

  // password checks
  if (password !== passwordConfirmation)
    return [
      localeText(
        'rw_register_msg_passwords_do_not_match',
        'Passwords do not match.'
      )
    ];

  const passwordErrors = [];
  if (password.length < 8 || password.length > 64)
    passwordErrors.push(
      localeText(
        'rw_register_msg_password_length_requirement',
        'Password length must be between 8 and 64 characters'
      )
    );
  if (!containsUppercase(password))
    passwordErrors.push(
      localeText(
        'rw_register_text_password_requirement_uppercase',
        'Password MUST have at least one uppercase letter'
      )
    );
  if (!containsLowercase(password))
    passwordErrors.push(
      localeText(
        'rw_register_text_password_requirement_lowercase',
        'Password MUST have at least one lowercase letter'
      )
    );
  if (!containsNumber(password))
    passwordErrors.push(
      localeText(
        'rw_register_text_password_requirement_number',
        'Password MUST have at least one number'
      )
    );
  if (!containsSpecialCharacter(password)) {
    const str1 =
      'Password MUST have at least one special character in the list: ~`';
    const str2 = `!@#$%^&*()_-+={[}]|:;“”\\'<,>.?`;
    passwordErrors.push(
      localeText(
        'rw_register_text_password_requirement_special_char',
        str1 + str2
      )
    );
  }
  return passwordErrors;
};

const getDateString = (timestamp, options) => {
  if (!timestamp) return;

  let date = options?.includes('date');
  let time = options?.includes('time');

  if (!date && !time) {
    date = true;
    time = true;
  }

  const d = new Date(timestamp);

  if (date && time) {
    return moment(d).format('MMM-D-YYYY h:mm A').toUpperCase();
  }

  if (date) {
    return moment(d).format('MMM-D-YYYY').toUpperCase();
  }

  // return just time in HH:MM AM/PM
  return moment(d).format('h:mm A').toUpperCase();
};

const getExpiryDate = (validToDate, sunsetDate) => {
  const validToDateObj = validToDate ? new Date(validToDate) : null;
  const sunsetDateObj = sunsetDate ? new Date(sunsetDate) : null;

  if (!validToDateObj) return getDateString(sunsetDateObj);
  if (validToDateObj < sunsetDateObj) return getDateString(validToDateObj);
  return getDateString(sunsetDateObj);
};

const getTicketActiveStatus = (localeText, validFrom, validTo, sunset) => {
  const validFromDateObj = validFrom ? new Date(validFrom) : null;
  const validToDateObj = validTo ? new Date(validTo) : null;
  const sunsetDateObj = sunset ? new Date(sunset) : null;
  const todayDateObj = new Date();

  let expDateObj = sunsetDateObj;
  if (validToDateObj && validToDateObj < sunsetDateObj)
    expDateObj = validToDateObj;

  if (expDateObj < todayDateObj) {
    return localeText('rw_title_expired', 'expired');
  }
  if (!validFromDateObj || validFromDateObj > todayDateObj) {
    return localeText('rw_title_inactive', 'inactive');
  }
  return localeText('rw_title_active', 'active');
};

const isAPassNotATicket = str => {
  return ['7day', '30day', '365day'].some(element =>
    str.toLowerCase().includes(element)
  );
};

const getPaymentMethodIcon = (content, pm) => {
  const assetBaseUrl = content?.config?.assetBaseUrl;

  if (!assetBaseUrl) {
    return null;
  }

  for (let card of ['mastercard', 'discover', 'visa', 'paypal']) {
    if (pm?.cardType?.toLowerCase().includes(card)) {
      return `${assetBaseUrl}/Common/common_card_${card}.png`;
    }
  }

  return `${assetBaseUrl}/Common/common_card_unknown.png`;
};
/**
 * Retrieves the input value by passing in an event and input name.
 *
 * @param {Event} event - The event containing the input element.
 * @param {string} name - The name attribute of the input element.
 * @returns {string} - The value of the input element.
 */
const getInputValueByName = (event, name) =>
  event?.target?.querySelector(`input[name="${name}"]`)?.value || null;

const getStationNameFromId = (localeText, content, stationId) => {
  const stationSlug = content?.menu?.nodes?.find(
    node => node.metaValue === stationId
  )?.displayItem;

  if (stationSlug) {
    return getDisplayItemTextPhraseFromSlug(localeText, content, stationSlug);
  }

  return null;
};

const getDisplayItemTextPhraseFromSlug = (localeText, content, slug) =>
  localeText(
    content?.catalog?.displayItems?.find(di => di.slug === slug)?.textPhrase,
    slug
  );

const getAccountBalanceInDollars = account => {
  return (
    (account?.transitAccounts?.[0].storedValue?.balance || 0) / 100
  ).toFixed(2);
};

const logger = logMsg => {
  if (['local', 'dev', 'test'].some(s => scope.includes(s)))
    console.log(logMsg);
};

const getAssociatedTransitCardFromTicket = (account, ticketObj) => {
  const transitCard = account.transitAccounts[0].transitCards.find(
    transitCard => {
      if (!transitCard.tickets) return false;
      const foundTransitCard = transitCard.tickets.find(ticket =>
        ticket.id === ticketObj.id ? true : false
      );
      return foundTransitCard ? true : false;
    }
  );
  return transitCard ? transitCard : null;
};

const showMsToClock = ms => {
  const date = new Date(ms);
  return `${date.getMinutes()}:${date
    .getSeconds()
    .toString()
    .padStart(2, '0')}`;
};

const getTicketStatus = (localeText, ticketObj) => {
  const {
    validFromDateTime: validFrom,
    validToDateTime: validTo,
    sunsetDateTime: sunset
  } = ticketObj;
  const validFromDateObj = validFrom ? new Date(validFrom) : null;
  const validToDateObj = validTo ? new Date(validTo) : null;
  const sunsetDateObj = sunset ? new Date(sunset) : null;
  const todayDateObj = new Date();

  if (!validFromDateObj || validFromDateObj > todayDateObj) {
    return localeText('rw_title_unused', 'unused');
  }
  if (
    (validToDateObj && validToDateObj < todayDateObj) ||
    (sunsetDateObj && sunsetDateObj < todayDateObj)
  ) {
    return localeText('rw_title_expired', 'expired');
  }
  return localeText('rw_title_in_use', 'in use');
};

const getAllTickets = (account, mobileFilter = false) => {
  const {
    onDevice = [],
    retrievable = [],
    unretrievable = []
  } = account?.tickets;

  return [
    ...new Map(
      [...onDevice, ...retrievable, ...unretrievable].map(obj => [obj.id, obj])
    ).values()
  ].sort((a, b) => parseInt(a.id) - parseInt(b.id));
};

const cardPaymentMethodExpired = paymentMethod => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const expYear = Number(paymentMethod.expirationYear);
  const expMonth = Number(paymentMethod.expirationMonth);
  if (!expYear || !expMonth) return false; // treat missing expiration as valid
  if (expYear > currentYear) return false;
  if (expYear === currentYear && expMonth >= currentMonth) return false;
  return true;
};

const cardPaymentMethodExists = account => {
  if (!account?.paymentMethods?.length) return false;

  return account.paymentMethods
    .filter(pm => pm.__typename === 'CardPaymentMethod')
    .some(pm => !cardPaymentMethodExpired(pm));
};

const paymentMethodExpiredStyling = paymentMethod => {
  const EXPIRED_STYLING = 'text-red';
  return cardPaymentMethodExpired(paymentMethod) ? EXPIRED_STYLING : '';
};

const assetImgUrl = (content, imgKey) => {
  const baseAssetUrl = content?.config?.assetBaseUrl || '';
  const imgPath = content?.config?.[imgKey] || '';

  if (baseAssetUrl && imgPath) {
    // Add a slash between baseUrl and imgPath if imgPath doesn't start with one
    const separator = imgPath.startsWith('/') ? '' : '/';
    return `${baseAssetUrl}${separator}${imgPath}`;
  }
  return null;
};

const shouldShowField = requirement =>
  requirement === 'required' || requirement === 'optional';

export {
  dollarAmount,
  getPasswordErrors,
  getDateString,
  getExpiryDate,
  getTicketActiveStatus,
  isAPassNotATicket,
  getPaymentMethodIcon,
  getInputValueByName,
  getStationNameFromId,
  getDisplayItemTextPhraseFromSlug,
  getAccountBalanceInDollars,
  logger,
  getAssociatedTransitCardFromTicket,
  paymentMethodExpiredStyling,
  showMsToClock,
  getTicketStatus,
  getAllTickets,
  cardPaymentMethodExists,
  assetImgUrl,
  shouldShowField
};
